import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from './components/Table';
import OrderDetailsModal from './components/OrderDetailsModal';

function OrdersTable() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/orders?limit=1000');
      setOrders(response.data.orders || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = React.useMemo(() => {
    let sortableOrders = [...orders];
    if (sortConfig.key) {
      sortableOrders.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key === 'customerName') {
          aValue = a.customerDetails?.name || '';
          bValue = b.customerDetails?.name || '';
        } else if (sortConfig.key === 'carCompany') {
          aValue = a.carDetails?.company || '';
          bValue = b.carDetails?.company || '';
        } else if (sortConfig.key === 'partName') {
          aValue = a.carDetails?.partName || '';
          bValue = b.carDetails?.partName || '';
        } else if (sortConfig.key === 'vendorName') {
          aValue = a.vendorDetails?.name || '';
          bValue = b.vendorDetails?.name || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOrders;
  }, [orders, sortConfig]);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { className: 'status-pending', label: 'Pending' },
      confirmed: { className: 'status-confirmed', label: 'Confirmed' },
      completed: { className: 'status-completed', label: 'Completed' },
      cancelled: { className: 'status-cancelled', label: 'Cancelled' }
    };
    const statusInfo = statusMap[status] || { className: 'status-pending', label: status };
    
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const columns = [
    {
      label: 'Order #',
      key: 'orderNumber',
    },
    {
      label: 'Customer',
      key: 'customerName',
      render: (row) => row.customerDetails?.name || 'N/A'
    },
    {
      label: 'Phone',
      key: 'phone',
      render: (row) => row.customerDetails?.phone || 'N/A'
    },
    {
      label: 'Car Company',
      key: 'carCompany',
      render: (row) => row.carDetails?.company || 'N/A'
    },
    {
      label: 'Model',
      key: 'model',
      render: (row) => row.carDetails?.model || 'N/A'
    },
    {
      label: 'Part Name',
      key: 'partName',
      render: (row) => row.carDetails?.partName || 'N/A'
    },
    {
      label: 'Vendor',
      key: 'vendorName',
      render: (row) => row.vendorDetails?.name || 'N/A'
    },
    {
      label: 'Price',
      key: 'price',
      render: (row) => (
        <span style={{ fontWeight: 'bold', color: '#059669' }}>
          AED {row.price || '0'}
        </span>
      )
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => getStatusBadge(row.status)
    },
    {
      label: 'Date',
      key: 'createdAt',
      render: (row) => new Date(row.createdAt).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },
    {
      label: 'Actions',
      key: 'actions',
      render: (row) => (
        <button
          className="btn-explore"
          onClick={() => setSelectedOrder(row)}
        >
          👁️ Explore
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={fetchOrders} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="orders-table-container">
      <Table
        columns={columns}
        TableData={sortedOrders}
        sortConfig={sortConfig}
        handleSort={handleSort}
        pagination={true}
        isSearchBar={true}
        headingDataOnTable="📦 Orders Management"
      />

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      <style jsx>{`
        .orders-table-container {
          padding: 20px;
        }

        .btn-explore {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .btn-explore:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .status-badge {
          display: inline-block;
          padding: 5px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .status-pending {
          background: #fef3c7;
          color: #d97706;
        }

        .status-confirmed {
          background: #d4f4e2;
          color: #059669;
        }

        .status-completed {
          background: #dbeafe;
          color: #2563eb;
        }

        .status-cancelled {
          background: #fee2e2;
          color: #dc2626;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 400px;
          gap: 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .btn-retry {
          background: #667eea;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-retry:hover {
          background: #5568d3;
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
}

export default OrdersTable;
